import { useContext, useEffect, useState, useRef } from "react";

import {
  Button,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import {
  SurveyConfiguration,
  getOrgConfigPortal,
  patchCallLog as patchCallLog_api,
  putCallLog as putCallLog_api,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useParams, useNavigate } from "react-router";

import ReportIcon from "@/common/ReportIcon";
import { getServiceConfig } from "@/common/serviceConfigurations";
import { mailTo } from "@/common/utils";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { OnlineReportingContext } from "@/components/online-reporting/context";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { IUserPermission } from "@/types";

import ReportFilterButtons from "./ReportFilterButtons";
import RubiMessageDiscoveryBanner from "./RubiMessageDiscoveryBanner";
import { useRubiMessageAccess } from "./useRubiMessageAccess";
import {
  generateTextMessageBody,
  generateGeneralReportingTextMessageBody,
  resolveApiRegion,
} from "./utils";

const CitizenEngage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();

  const { isDevEnv } = useDevEnvironment();
  const { fetchReportConfigurations, reportConfigurations } = useContext(
    OnlineReportingContext,
  );
  const { currentUser } = useContext(AuthStateContext);
  const rubiMessageAccess = useRubiMessageAccess();
  const [reportPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );
  const canViewCitizenEngage = reportPermissions
    ? reportPermissions.canViewCitizenEngageRV2
    : true;

  const [callerFirstName, setCallerFirstName] = useState("");
  const [callerLastName, setCallerLastName] = useState("");
  const [areaCode, setAreaCode] = useState("+1");
  const [callerPhoneNumber, setCallerPhoneNumber] = useState("");
  const [selectedReport, setSelectedReport] =
    useState<SurveyConfiguration | null>(null);
  const [isGeneralReportingSelected, setIsGeneralReportingSelected] =
    useState(false);
  const [
    citizenEngageGeneralReportingEnabled,
    setCitizenEngageGeneralReportingEnabled,
  ] = useState(false);
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString(),
  );

  useEffect(() => {
    fetchReportConfigurations(orgId ?? "");
    async function fetchOrgConfig() {
      const response = (
        await getOrgConfigPortal({ path: { org_id: orgId ?? "" } })
      ).data;
      if (response) {
        setCitizenEngageGeneralReportingEnabled(
          response.citizen_engage_general_reporting_enabled ?? false,
        );
      }
    }
    fetchOrgConfig();
  }, []);

  const organizationConfig = getServiceConfig(
    orgId ?? currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );
  const { servicesURLSlug, fullName } = organizationConfig;
  const apiRegion = resolveApiRegion({
    sessionApiRegion: sessionStorage.getItem("apiRegion"),
    organizationApiRegion: organizationConfig.apiRegion,
    userApiRegion: currentUser?.api_region,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const validatePhoneNumber = (phoneNumber: string) => {
    return phoneNumber.match(/^\d{10}$/);
  };

  const putCallLog = async () => {
    try {
      const res = (await putCallLog_api({ path: { org_id: orgId ?? "" } }))
        .data!;
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const patchCallLog = async (callLogId: string, referenceId: string) => {
    try {
      if (!selectedReport && !isGeneralReportingSelected) {
        toast.error("⚠️ Please select a report.");
        return;
      }

      const reportType = isGeneralReportingSelected
        ? "general_reporting_page"
        : selectedReport!.survey_class;

      const message = isGeneralReportingSelected
        ? generateGeneralReportingTextMessageBody(
            callerFirstName,
            callerLastName,
            servicesURLSlug ?? "",
            fullName ?? "",
            isDevEnv,
            referenceId,
            apiRegion,
          )
        : generateTextMessageBody(
            selectedReport!.name,
            callerFirstName,
            callerLastName,
            servicesURLSlug ?? "",
            selectedReport!,
            fullName ?? "",
            isDevEnv,
            referenceId,
            apiRegion,
          );

      await patchCallLog_api({
        path: { org_id: orgId ?? "", call_log_id: callLogId },
        body: {
          first_name: callerFirstName ?? null,
          last_name: callerLastName ?? null,
          phone_number: `${areaCode}${callerPhoneNumber}`,
          email: null,
          location: null,
          internal_notes: null,
          report_type: reportType,
          message,
        },
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const phoneNumberInputRef = useRef<HTMLInputElement>(null);

  const handleSendText = async () => {
    if (!validatePhoneNumber(callerPhoneNumber)) {
      toast.error(
        "⚠️ Invalid phone number entered. Please enter a 10-digit number.",
      );
      phoneNumberInputRef.current?.focus();
      return;
    }
    if (!selectedReport && !isGeneralReportingSelected) {
      toast.error("⚠️ Please select a report.");
      return;
    }

    try {
      const response = await putCallLog();
      await patchCallLog(response.call_log_id, response.reference_id);
      toast.success("✅ Report link sent successfully!");
    } catch (err) {
      console.error("Error in creating or updating call log", err);
      toast.error("❌ Failed to send report link. Please try again.");
    }
  };

  const handleClearForm = () => {
    setCallerFirstName("");
    setCallerLastName("");
    setAreaCode("+1");
    setCallerPhoneNumber("");
    setSelectedReport(null);
    setIsGeneralReportingSelected(false);
  };

  if (!canViewCitizenEngage) {
    return (
      <DisabledComponentDialog
        isDialogOpen={true}
        handleClose={() => {
          navigate(`/`);
        }}
        title="You do not have access to CitizenENGAGE."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to CitizenENGAGE",
                body: `Hello Rubicon Support Team, 

                I would like to request access to CitizenENGAGE.

                Thank you. 

                Sincerely,

                ${currentUser?.first_name} ${currentUser?.last_name} 
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  }

  const rubiMessageBanner = (
    <RubiMessageDiscoveryBanner
      access={rubiMessageAccess}
      phoneNumber={
        validatePhoneNumber(callerPhoneNumber)
          ? `${areaCode}${callerPhoneNumber}`
          : null
      }
    />
  );

  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "auto" }}>
      {rubiMessageAccess === "user" && rubiMessageBanner}
      <Paper elevation={6} style={{ padding: "30px", marginTop: "20px" }}>
        <Typography
          variant="h4"
          color="primary"
          style={{ marginBottom: "8px" }}
        >
          <b>CitizenENGAGE</b>
        </Typography>

        <Typography variant="body1" style={{ marginBottom: "8px" }}>
          This tool allows members of {fullName} to directly text a link to a{" "}
          <b>non-emergency report</b> to a citizen.
        </Typography>

        <Typography
          variant="caption"
          display="block"
          style={{ marginBottom: "8px", fontStyle: "italic" }}
        >
          Current Time: {currentTime}
        </Typography>

        <Grid container spacing={1}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              variant="outlined"
              label="Caller First Name"
              fullWidth
              margin="dense"
              value={callerFirstName}
              onChange={(e) => setCallerFirstName(e.target.value)}
              style={{ marginBottom: "8px" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              variant="outlined"
              label="Caller Last Name"
              fullWidth
              margin="dense"
              value={callerLastName}
              onChange={(e) => setCallerLastName(e.target.value)}
              style={{ marginBottom: "8px" }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} alignItems="center">
          <Grid size={{ xs: 4 }}>
            <TextField
              select
              label="Area Code"
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value)}
              variant="outlined"
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
              style={{ marginBottom: "8px" }}
            >
              <MenuItem value="+1">+1 (USA/Canada)</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 8 }}>
            <TextField
              inputRef={phoneNumberInputRef}
              variant="outlined"
              label="Caller Phone Number *"
              fullWidth
              margin="dense"
              value={callerPhoneNumber}
              onChange={(e) => setCallerPhoneNumber(e.target.value)}
              style={{ marginBottom: "8px" }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        {citizenEngageGeneralReportingEnabled && (
          <>
            <Button
              variant={isGeneralReportingSelected ? "contained" : "outlined"}
              color="primary"
              fullWidth
              onClick={() => {
                setIsGeneralReportingSelected(true);
                setSelectedReport(null);
              }}
              startIcon={
                <ReportIcon
                  src="/report-icons/choose.png"
                  alt="General Reporting Page"
                />
              }
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                padding: 1,
                marginTop: "16px",
                textTransform: "none",
                border: isGeneralReportingSelected ? 2 : 1,
                borderColor: isGeneralReportingSelected
                  ? "primary.main"
                  : "divider",
                backgroundColor: isGeneralReportingSelected
                  ? "primary.light"
                  : "background.paper",
                color: isGeneralReportingSelected
                  ? "primary.contrastText"
                  : "text.primary",
                "&:hover": {
                  backgroundColor: isGeneralReportingSelected
                    ? "primary.main"
                    : "action.hover",
                },
              }}
            >
              GENERAL REPORTING PAGE
            </Button>
            <Typography
              variant="caption"
              display="block"
              style={{ marginTop: "4px", fontStyle: "italic" }}
            >
              Selecting this option will take the citizen to your reporting
              landing and FAQ page
            </Typography>
            <Divider style={{ margin: "16px 0" }} />
          </>
        )}
        {reportConfigurations && (
          <ReportFilterButtons
            citizenEngageDisableHiddenContactNumber={
              organizationConfig.citizenEngageDisableHiddenContactNumber ?? ""
            }
            citizenEngageDisableReportsContactNumber={
              organizationConfig.citizenEngageDisableReportsContactNumber ?? ""
            }
            reports={reportConfigurations}
            selectedReport={selectedReport}
            onReportSelect={(report) => {
              setSelectedReport(report);
              setIsGeneralReportingSelected(false);
            }}
          />
        )}

        <Grid
          container
          spacing={1}
          justifyContent="space-between"
          style={{ marginTop: "20px" }}
        >
          <Grid size={{ xs: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearForm}
              fullWidth
              // Rest on the same faint border as the template boxes so the
              // theme's full-contrast hover border reads as a hover in dark mode.
              sx={(theme) =>
                theme.applyStyles("dark", { borderColor: "divider" })
              }
            >
              Clear
            </Button>
          </Grid>
          <Grid size={{ xs: 10 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendText}
              fullWidth
            >
              Send Text
            </Button>
          </Grid>
        </Grid>
      </Paper>
      {rubiMessageAccess !== "user" && rubiMessageBanner}
    </div>
  );
};

export default CitizenEngage;
